import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMatches } from '@remix-run/react';

import { COMMUNITY_ROUTE_IDS } from '~/constants/community';
import type { CommunitySpace } from '~/interfaces/community';

import { useDirectoryName } from './useDirectoryName';

export type MobilePlusAction = 'join' | 'create' | 'none';

type MobilePageInfo = {
  title: string;
  readOnly: boolean;
  showChannelDropdown: boolean;
  plusAction: MobilePlusAction;
  showTopbar: boolean;
  showPageHeader: boolean;
};

export const useMobilePageInfo = (spaces: CommunitySpace[]): MobilePageInfo => {
  const matches = useMatches();
  const { t } = useTranslation();
  const directoryName = useDirectoryName();

  const leafRoute = matches[matches.length - 1];

  return useMemo(() => {
    switch (leafRoute?.id) {
      case COMMUNITY_ROUTE_IDS.messages:
        return {
          title: t('community.sidebar.messages'),
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: true,
          showPageHeader: false,
        };
      case COMMUNITY_ROUTE_IDS.events:
        return {
          title: t('community.sidebar.events'),
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: true,
          showPageHeader: true,
        };
      case COMMUNITY_ROUTE_IDS.directory:
        return {
          title: directoryName,
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: true,
          showPageHeader: false,
        };
      case COMMUNITY_ROUTE_IDS.notifications:
        return {
          title: t('community.sidebar.notifications'),
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: false,
          showPageHeader: false,
        };
      case COMMUNITY_ROUTE_IDS.onboard:
        return {
          title: t('community.onboard.title'),
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: true,
          showPageHeader: false,
        };
      case COMMUNITY_ROUTE_IDS.space: {
        if (leafRoute.data && typeof leafRoute.data === 'object' && 'unavailableChannel' in leafRoute.data) {
          return {
            title: '',
            readOnly: false,
            showChannelDropdown: false,
            plusAction: 'none',
            showTopbar: true,
            showPageHeader: false,
          };
        }
        const slug = (leafRoute.params as Record<string, string>).slug;
        const space = spaces.find((s) => s.slug === slug);
        return {
          title: space?.name ?? slug ?? '',
          readOnly: space?.read_only ?? false,
          showChannelDropdown: true,
          plusAction: 'join',
          showTopbar: true,
          showPageHeader: true,
        };
      }
      case COMMUNITY_ROUTE_IDS.feed:
        return {
          title: '',
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'create',
          showTopbar: true,
          showPageHeader: false,
        };
      default:
        return {
          title: '',
          readOnly: false,
          showChannelDropdown: false,
          plusAction: 'none',
          showTopbar: true,
          showPageHeader: false,
        };
    }
  }, [directoryName, leafRoute?.id, leafRoute?.params, leafRoute?.data, spaces, t]);
};
